import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useDisclosure } from '@chakra-ui/react';
import {
  AgentDelegationAvailability,
  AgentDelegationKind,
  AgentDelegationTarget,
  AgentDelegationTaskType,
  InboxEntry,
  LocalCodingAgentRuntime,
  LocalDevice,
  McpConnectionStatus,
} from '@this+that/models';
import log from 'loglevel';

import { useDoBoxExecutorFlags } from '../../../../../../hooks/DoBox/useDoBoxExecutorFlags';
import { startAgentSetupForEntry } from '../../../../../../hooks/DoBox/useStartAgentSetupForEntry';
import { useActiveInboxEntriesSlice } from '../../../../../../hooks/InboxEntries/useActiveInboxEntriesSlice';
import { useCustomToast } from '../../../../../../hooks/useCustomToast';
import { useOpenAgentConversationInDock } from '../../../../../../hooks/useOpenAgentConversationInDock';
import { APP_PAGES } from '../../../../../../routes/constants';
import { useGetAgentDelegationTargets } from '../../../../../../services/agentDelegation.service';
import {
  useCancelInboxEntryDelegation,
  useDelegateInboxEntryToAgent,
  usePauseInboxEntryDelegation,
  useResumeInboxEntryDelegation,
} from '../../../../../../services/inboxEntries.service';
import { useGetMyLocalDevices } from '../../../../../../services/localDevices.service';
import {
  McpConnection,
  useGetUserMcpConnections,
} from '../../../../../../services/mcpConnections.service';
import { useAgentDelegationTargetsStore } from '../../../../../../store/AgentDelegationTargets';
import { useFloatingAssistantStore } from '../../../../../../store/FloatingAssistant';
import { pushGTMEvent } from '../../../../../../utils/analytics/gtm';
import {
  AGENT_TARGETS_MAX_AGE_MS,
  FORCE_REFETCH_MAX_AGE_MS,
} from '../../../../../../utils/constants/agentDelegationTargets';
import {
  buildDelegationInputVariables as buildDelegationInputVariablesShared,
  resolveTaskTypeForRuntime,
} from '../../../../../../utils/delegation/buildDelegationInputVariables';
import {
  findNewRun,
  isRunActive,
  listVisibleTicketRuns,
  toDelegationIdArg,
} from '../../../../../../utils/delegation/listTicketRuns';
import { resolveDelegateErrorI18nKey } from '../../../../../../utils/delegation/resolveDelegateErrorKey';
import {
  canOfferExecuteTaskType,
  resolveDefaultDelegationTaskType,
} from '../../../../../../utils/delegation/resolveDelegationTaskType';
import { resolveTicketRunFocus } from '../../../../../../utils/delegation/resolveRunPriority';
import { normalizeForSearch } from '../../../../../../utils/string';
import { buildDoBoxDelegateContext } from '../../../../Assistant/utils/delegateContextUtils';
import {
  AGENT_PROFILE_GENERIC_VALUE,
  DELEGATE_TARGET_SEARCH_VISIBILITY_THRESHOLD,
} from '../constants';
import { getDefaultModelForRuntime } from '../delegateRuntimeModels';

const INTEGRATION_PATH_WILDCARD = '/*';

export enum LocalMachineRowStatus {
  SELECTABLE = 'SELECTABLE',
  CONNECTION_ERROR = 'CONNECTION_ERROR',
  OFFLINE = 'OFFLINE',
  CHECKING = 'CHECKING',
}

export interface DoBoxDelegateLocalMachineRow {
  device: LocalDevice;
  target: AgentDelegationTarget | null;
  connection: McpConnection | undefined;
  status: LocalMachineRowStatus;
}

/** CONNECTION_ERROR is reserved for a machine that IS reachable and still cannot
 *  report its tools. A machine that is merely starting up — connected with no
 *  delegation target yet, or not connected at all — is unavailable, not broken. */
export function resolveLocalMachineRowStatus(
  device: LocalDevice,
  target: AgentDelegationTarget | null,
  connection: McpConnection | undefined,
  isCheckingTarget = false,
): LocalMachineRowStatus {
  if (!device.isConnected) {
    return LocalMachineRowStatus.OFFLINE;
  }
  if (connection?.status === McpConnectionStatus.ERROR) {
    return LocalMachineRowStatus.CONNECTION_ERROR;
  }
  if (!target && isCheckingTarget) {
    return LocalMachineRowStatus.CHECKING;
  }
  if (!target || target.availability === AgentDelegationAvailability.OFFLINE) {
    return LocalMachineRowStatus.OFFLINE;
  }
  return LocalMachineRowStatus.SELECTABLE;
}

function buildLocalMachineRows(
  devices: LocalDevice[],
  connectionsById: Map<string, McpConnection>,
  targetsByConnectionId: Map<string, AgentDelegationTarget>,
  isCheckingTargets: boolean,
): DoBoxDelegateLocalMachineRow[] {
  return devices.map((device) => {
    const connection = device.mcpConnectionId
      ? connectionsById.get(device.mcpConnectionId)
      : undefined;
    const target = device.mcpConnectionId
      ? (targetsByConnectionId.get(device.mcpConnectionId) ?? null)
      : null;
    return {
      device,
      target,
      connection,
      status: resolveLocalMachineRowStatus(
        device,
        target,
        connection,
        isCheckingTargets && Boolean(device.mcpConnectionId),
      ),
    };
  });
}

export enum DoBoxDelegateMenuStep {
  ROOT = 'root',
  TASK_TYPE = 'task-type',
  ACTIVE = 'active',
}

const AVAILABILITY_SORT_RANK: Record<AgentDelegationAvailability, number> = {
  [AgentDelegationAvailability.AVAILABLE]: 0,
  [AgentDelegationAvailability.BUSY]: 1,
  [AgentDelegationAvailability.OFFLINE]: 2,
};

const DEFAULT_LOCAL_RUNTIME = LocalCodingAgentRuntime.CLAUDE;

export function getPreferredLocalRuntime(
  target: AgentDelegationTarget,
): LocalCodingAgentRuntime {
  return target.availableLocalRuntimes?.[0] ?? DEFAULT_LOCAL_RUNTIME;
}

function compareTargetsByAvailabilityThenName(
  a: AgentDelegationTarget,
  b: AgentDelegationTarget,
): number {
  const availabilityDiff =
    AVAILABILITY_SORT_RANK[a.availability] -
    AVAILABILITY_SORT_RANK[b.availability];
  if (availabilityDiff !== 0) {
    return availabilityDiff;
  }
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}

export function loadTargetsOnOpen(
  getTargets: (
    kinds?: AgentDelegationKind[],
  ) => Promise<AgentDelegationTarget[]>,
): void {
  const { ensureFresh } = useAgentDelegationTargetsStore.getState();
  const logFailure = (error: unknown) => {
    log.error('useDoBoxDelegateMenu: failed to load targets', error);
  };
  ensureFresh(
    AgentDelegationKind.AGENT_RUN,
    () => getTargets([AgentDelegationKind.AGENT_RUN]),
    AGENT_TARGETS_MAX_AGE_MS,
  ).catch(logFailure);
  ensureFresh(
    AgentDelegationKind.LOCAL_MCP_CODING,
    () => getTargets([AgentDelegationKind.LOCAL_MCP_CODING]),
    FORCE_REFETCH_MAX_AGE_MS,
  ).catch(logFailure);
}

function invalidateLocalTargets(): void {
  useAgentDelegationTargetsStore
    .getState()
    .invalidate(AgentDelegationKind.LOCAL_MCP_CODING);
}

export function hasActiveDelegation(inboxEntry: InboxEntry): boolean {
  return listVisibleTicketRuns(inboxEntry).some((run) =>
    isRunActive(run.delegation),
  );
}

export function useDoBoxDelegateMenu(
  inboxEntry: InboxEntry,
  showPlanReviewTrigger = false,
) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { errorMessage, successMessage } = useCustomToast();
  const { flags: doBoxExecutorFlags } = useDoBoxExecutorFlags();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [step, setStep] = useState<DoBoxDelegateMenuStep>(
    DoBoxDelegateMenuStep.ROOT,
  );
  const [isPlanReviewOpen, setIsPlanReviewOpen] = useState(false);
  const [planReviewRunId, setPlanReviewRunId] = useState<string | undefined>(
    undefined,
  );
  const [selectedTarget, setSelectedTarget] =
    useState<AgentDelegationTarget | null>(null);
  const [isDelegating, setIsDelegating] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [selectedTaskType, setSelectedTaskType] = useState(
    AgentDelegationTaskType.TECHNICAL_ANALYSIS,
  );
  const [selectedLocalRuntime, setSelectedLocalRuntime] = useState(
    DEFAULT_LOCAL_RUNTIME,
  );
  const [selectedLocalModel, setSelectedLocalModel] = useState(
    getDefaultModelForRuntime(DEFAULT_LOCAL_RUNTIME),
  );
  const [selectedAgentProfile, setSelectedAgentProfile] = useState(
    AGENT_PROFILE_GENERIC_VALUE,
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [isPairDeviceModalOpen, setIsPairDeviceModalOpen] = useState(false);
  const [isCreateAgentWizardOpen, setIsCreateAgentWizardOpen] = useState(false);

  const openWithContext = useFloatingAssistantStore((s) => s.openWithContext);
  const requestHostedPanelFocus = useFloatingAssistantStore(
    (s) => s.requestHostedPanelFocus,
  );
  const { updateInboxEntry } = useActiveInboxEntriesSlice();
  const { getAgentDelegationTargets } = useGetAgentDelegationTargets();
  const agentSlice = useAgentDelegationTargetsStore(
    (s) => s.slices[AgentDelegationKind.AGENT_RUN],
  );
  const localSlice = useAgentDelegationTargetsStore(
    (s) => s.slices[AgentDelegationKind.LOCAL_MCP_CODING],
  );
  const focus = resolveTicketRunFocus(inboxEntry);
  const focusDelegation = focus.run?.delegation;
  const focusDelegationId = toDelegationIdArg(focusDelegation);
  const canStartAnotherAgent =
    doBoxExecutorFlags.concurrentAgentRuns && focus.run !== null;
  const { delegateInboxEntryToAgent } = useDelegateInboxEntryToAgent();
  const { cancelInboxEntryDelegation } = useCancelInboxEntryDelegation();
  const { pauseInboxEntryDelegation } = usePauseInboxEntryDelegation();
  const { resumeInboxEntryDelegation } = useResumeInboxEntryDelegation();
  const openConversationInDock = useOpenAgentConversationInDock();
  const { data: localDevicesData, loading: isLoadingLocalDevices } =
    useGetMyLocalDevices({ skip: !isOpen });
  const { data: mcpConnectionsData, loading: isLoadingMcpConnections } =
    useGetUserMcpConnections({ skip: !isOpen });

  const resetMenuState = useCallback(() => {
    // step is intentionally left as-is: Chakra keeps MenuList rendered during
    // its exit animation (no close-complete hook), so resetting it here would
    // flash ACTIVE -> ROOT mid fade-out; handleOpen sets the right step next time.
    setSelectedTarget(null);
    setInstructions('');
    setIsConfirmingCancel(false);
    setSelectedTaskType(AgentDelegationTaskType.TECHNICAL_ANALYSIS);
    setSelectedLocalRuntime(DEFAULT_LOCAL_RUNTIME);
    setSelectedLocalModel(getDefaultModelForRuntime(DEFAULT_LOCAL_RUNTIME));
    setSelectedAgentProfile(AGENT_PROFILE_GENERIC_VALUE);
    setSearchQuery('');
  }, []);

  const handleOpen = useCallback(() => {
    onOpen();
    // Not cancelled (cancelInboxEntryDelegation clears the run entirely)
    // is the only bar for reopening into the ACTIVE step — including DONE/FAILED,
    // so a finished delegation is still reachable instead of looking undelegated.
    // A chat-link delegation is not user-visible, so it falls through to ROOT
    // like no delegation at all.
    if (focus.run !== null) {
      setStep(DoBoxDelegateMenuStep.ACTIVE);
      return;
    }
    setStep(DoBoxDelegateMenuStep.ROOT);
    loadTargetsOnOpen(getAgentDelegationTargets);
  }, [onOpen, getAgentDelegationTargets, inboxEntry, focus.run]);

  const handleClose = useCallback(() => {
    onClose();
    resetMenuState();
  }, [onClose, resetMenuState]);

  const handleAskAssistant = useCallback(() => {
    pushGTMEvent({
      event: 'delegate_button_clicked',
      source_page: 'dobox',
      item_source: 'task',
      item_id: inboxEntry.id,
    });
    // The hosted panel (desktop column / mobile sheet already attached to this
    // entry) is a live session — openWithContext would instead stash a
    // pendingInputContext the floating drawer never reads while hosted, and the
    // panel the user is already looking at wouldn't move.
    const { hostContext } = useFloatingAssistantStore.getState();
    if (hostContext?.kind === 'dobox' && hostContext.itemId === inboxEntry.id) {
      requestHostedPanelFocus();
    } else {
      openWithContext(buildDoBoxDelegateContext(inboxEntry));
    }
    handleClose();
  }, [inboxEntry, openWithContext, requestHostedPanelFocus, handleClose]);

  const handleSelectTarget = useCallback(
    (target: AgentDelegationTarget) => {
      const preferredRuntime = getPreferredLocalRuntime(target);
      setSelectedTarget(target);
      setSelectedTaskType(
        resolveDefaultDelegationTaskType(
          target,
          doBoxExecutorFlags.executeDelegation,
        ),
      );
      setSelectedLocalRuntime(preferredRuntime);
      setSelectedLocalModel(getDefaultModelForRuntime(preferredRuntime));
      setSelectedAgentProfile(AGENT_PROFILE_GENERIC_VALUE);
      setStep(DoBoxDelegateMenuStep.TASK_TYPE);
    },
    [doBoxExecutorFlags.executeDelegation],
  );

  const handleStartAnotherAgent = useCallback(() => {
    setStep(DoBoxDelegateMenuStep.ROOT);
    loadTargetsOnOpen(getAgentDelegationTargets);
  }, [getAgentDelegationTargets]);

  const handleBack = useCallback(() => {
    setStep(DoBoxDelegateMenuStep.ROOT);
    setSelectedTarget(null);
    setSelectedTaskType(AgentDelegationTaskType.TECHNICAL_ANALYSIS);
  }, []);

  const canStartAgentSetup =
    doBoxExecutorFlags.executor && doBoxExecutorFlags.agentSetup;
  const inboxEntryId = inboxEntry.id;
  const inboxEntryTaskGroupId = inboxEntry.taskGroupId;
  const handleCreateNewAgent = useCallback(() => {
    handleClose();
    const hostedEntryId =
      useFloatingAssistantStore.getState().hostExecutorTarget?.entryId;
    if (canStartAgentSetup && hostedEntryId === inboxEntryId) {
      startAgentSetupForEntry({
        itemId: inboxEntryId,
        taskGroupId: inboxEntryTaskGroupId,
      });
      requestHostedPanelFocus();
      return;
    }
    setIsCreateAgentWizardOpen(true);
  }, [
    handleClose,
    canStartAgentSetup,
    inboxEntryId,
    inboxEntryTaskGroupId,
    requestHostedPanelFocus,
  ]);

  const handleCloseCreateAgentWizard = useCallback(() => {
    setIsCreateAgentWizardOpen(false);
  }, []);

  const handlePairDevice = useCallback(() => {
    handleClose();
    setIsPairDeviceModalOpen(true);
  }, [handleClose]);

  const handleClosePairDeviceModal = useCallback(() => {
    setIsPairDeviceModalOpen(false);
  }, []);

  const handleFixLocalMachineConnection = useCallback(() => {
    handleClose();
    navigate(APP_PAGES.INTEGRATION.replace(INTEGRATION_PATH_WILDCARD, ''));
  }, [handleClose, navigate]);

  const handleInstructionsChange = useCallback((value: string) => {
    setInstructions(value);
  }, []);

  const handleSelectTaskType = useCallback(
    (taskType: AgentDelegationTaskType) => {
      setSelectedTaskType(taskType);
    },
    [],
  );

  const handleSelectLocalRuntime = useCallback(
    (runtime: LocalCodingAgentRuntime) => {
      setSelectedLocalRuntime(runtime);
      setSelectedLocalModel(getDefaultModelForRuntime(runtime));
      setSelectedTaskType((previousTaskType) =>
        resolveTaskTypeForRuntime(runtime, previousTaskType),
      );
    },
    [],
  );

  const handleSelectLocalModel = useCallback((model: string) => {
    setSelectedLocalModel(model);
  }, []);

  const handleSelectAgentProfile = useCallback((profile: string) => {
    setSelectedAgentProfile(profile);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const buildDelegationInputVariables = useCallback(
    (target: AgentDelegationTarget, taskType: AgentDelegationTaskType) =>
      buildDelegationInputVariablesShared({
        target,
        taskType,
        selectedLocalRuntime,
        selectedLocalModel,
        selectedAgentProfile,
        instructions,
      }),
    [
      instructions,
      selectedLocalRuntime,
      selectedLocalModel,
      selectedAgentProfile,
    ],
  );

  const handleDelegate = useCallback(async () => {
    if (!selectedTarget) {
      return;
    }
    setIsDelegating(true);
    try {
      const updatedEntry = await delegateInboxEntryToAgent(
        inboxEntry.taskGroupId,
        inboxEntry.id,
        buildDelegationInputVariables(selectedTarget, selectedTaskType),
      );
      if (updatedEntry) {
        updateInboxEntry(updatedEntry);
      }
      invalidateLocalTargets();
      handleClose();
      if (showPlanReviewTrigger) {
        const startedRun = updatedEntry
          ? findNewRun(inboxEntry, updatedEntry)
          : undefined;
        setPlanReviewRunId(startedRun?.runId);
        setIsPlanReviewOpen(true);
      }
    } catch (error) {
      log.error('useDoBoxDelegateMenu: delegation failed', error);
      errorMessage(t(resolveDelegateErrorI18nKey(error)));
    } finally {
      setIsDelegating(false);
    }
  }, [
    selectedTarget,
    selectedTaskType,
    selectedLocalRuntime,
    selectedLocalModel,
    selectedAgentProfile,
    delegateInboxEntryToAgent,
    inboxEntry,
    buildDelegationInputVariables,
    updateInboxEntry,
    t,
    handleClose,
    errorMessage,
    showPlanReviewTrigger,
  ]);

  const handleDiscussActiveDelegation = useCallback(() => {
    const conversationId = focusDelegation?.conversationId;
    if (!conversationId) {
      return;
    }
    openConversationInDock(conversationId);
    handleClose();
  }, [focusDelegation?.conversationId, openConversationInDock, handleClose]);

  const handleCancelClick = useCallback(() => {
    setIsConfirmingCancel(true);
  }, []);

  const handleCancelDismiss = useCallback(() => {
    setIsConfirmingCancel(false);
  }, []);

  const handleCancelConfirm = useCallback(async () => {
    setIsCancelling(true);
    try {
      const updatedEntry = await cancelInboxEntryDelegation(
        inboxEntry.taskGroupId,
        inboxEntry.id,
        focusDelegationId,
      );
      if (updatedEntry) {
        updateInboxEntry(updatedEntry);
        successMessage(t('doBox.delegation.success.canceled'));
      }
      invalidateLocalTargets();
      handleClose();
    } catch (error) {
      log.error('useDoBoxDelegateMenu: cancel delegation failed', error);
      errorMessage(t('doBox.delegation.errors.cancelFailed'));
    } finally {
      setIsCancelling(false);
    }
  }, [
    cancelInboxEntryDelegation,
    inboxEntry.taskGroupId,
    inboxEntry.id,
    focusDelegationId,
    updateInboxEntry,
    successMessage,
    t,
    handleClose,
    errorMessage,
  ]);

  const handlePause = useCallback(async () => {
    setIsPausing(true);
    try {
      const updatedEntry = await pauseInboxEntryDelegation(
        inboxEntry.taskGroupId,
        inboxEntry.id,
        focusDelegationId,
      );
      if (updatedEntry) {
        updateInboxEntry(updatedEntry);
        successMessage(t('doBox.delegation.success.paused'));
      }
    } catch (error) {
      log.error('useDoBoxDelegateMenu: pause delegation failed', error);
      errorMessage(t('doBox.delegation.errors.pauseFailed'));
    } finally {
      setIsPausing(false);
    }
  }, [
    pauseInboxEntryDelegation,
    inboxEntry.taskGroupId,
    inboxEntry.id,
    focusDelegationId,
    updateInboxEntry,
    successMessage,
    t,
    errorMessage,
  ]);

  const handleResume = useCallback(async () => {
    setIsResuming(true);
    try {
      const updatedEntry = await resumeInboxEntryDelegation(
        inboxEntry.taskGroupId,
        inboxEntry.id,
        focusDelegationId,
      );
      if (updatedEntry) {
        updateInboxEntry(updatedEntry);
        successMessage(t('doBox.delegation.success.resumed'));
      }
    } catch (error) {
      log.error('useDoBoxDelegateMenu: resume delegation failed', error);
      errorMessage(t('doBox.delegation.errors.resumeFailed'));
    } finally {
      setIsResuming(false);
    }
  }, [
    resumeInboxEntryDelegation,
    inboxEntry.taskGroupId,
    inboxEntry.id,
    focusDelegationId,
    updateInboxEntry,
    successMessage,
    t,
    errorMessage,
  ]);

  const agentTargets = useMemo(
    () =>
      agentSlice.targets.filter(
        (target) => target.kind === AgentDelegationKind.AGENT_RUN,
      ),
    [agentSlice.targets],
  );

  const machineTargets = useMemo(
    () =>
      localSlice.targets
        .filter(
          (target) => target.kind === AgentDelegationKind.LOCAL_MCP_CODING,
        )
        .sort(compareTargetsByAvailabilityThenName),
    [localSlice.targets],
  );

  const targets = useMemo(
    () => [...agentTargets, ...machineTargets],
    [agentTargets, machineTargets],
  );

  const isLoadingAgentTargets =
    agentSlice.isLoading && agentSlice.fetchedAt === null;
  const isLoadingLocalTargets =
    localSlice.isLoading && localSlice.fetchedAt === null;

  const localDevices = useMemo(
    () => localDevicesData?.getMyLocalDevices ?? [],
    [localDevicesData],
  );

  const mcpConnectionsById = useMemo(() => {
    const map = new Map<string, McpConnection>();
    for (const connection of mcpConnectionsData?.getUserMcpConnections ?? []) {
      map.set(connection.connectionId, connection);
    }
    return map;
  }, [mcpConnectionsData]);

  const machineTargetsByConnectionId = useMemo(() => {
    const map = new Map<string, AgentDelegationTarget>();
    for (const target of machineTargets) {
      map.set(target.id, target);
    }
    return map;
  }, [machineTargets]);

  const localMachineRows = useMemo(
    () =>
      buildLocalMachineRows(
        localDevices,
        mcpConnectionsById,
        machineTargetsByConnectionId,
        isLoadingLocalTargets,
      ),
    [
      localDevices,
      mcpConnectionsById,
      machineTargetsByConnectionId,
      isLoadingLocalTargets,
    ],
  );

  const isLoadingLocalMachineSection =
    isLoadingLocalDevices || isLoadingMcpConnections;

  const shouldShowSearch =
    agentTargets.length > DELEGATE_TARGET_SEARCH_VISIBILITY_THRESHOLD;

  const filteredAgentTargets = useMemo(() => {
    const normalizedQuery = normalizeForSearch(searchQuery.trim());
    const matchingTargets = normalizedQuery
      ? agentTargets.filter((target) =>
          normalizeForSearch(target.name).includes(normalizedQuery),
        )
      : agentTargets;
    return [...matchingTargets].sort(compareTargetsByAvailabilityThenName);
  }, [agentTargets, searchQuery]);

  const canOfferExecute = selectedTarget
    ? canOfferExecuteTaskType(
        selectedTarget,
        doBoxExecutorFlags.executeDelegation,
      )
    : false;

  return {
    isOpen,
    isPlanReviewOpen,
    setIsPlanReviewOpen,
    planReviewRunId,
    setPlanReviewRunId,
    focusDelegation,
    focusRunId: focus.run?.runId,
    otherActiveRunCount: focus.otherActiveCount,
    canStartAnotherAgent,
    step,
    targets,
    agentTargets,
    filteredAgentTargets,
    machineTargets,
    localMachineRows,
    isLoadingLocalMachineSection,
    isPairDeviceModalOpen,
    isCreateAgentWizardOpen,
    canStartAgentSetup,
    shouldShowSearch,
    searchQuery,
    selectedTarget,
    selectedTaskType,
    canOfferExecute,
    selectedLocalRuntime,
    selectedLocalModel,
    selectedAgentProfile,
    isLoadingAgentTargets,
    isLoadingLocalTargets,
    isDelegating,
    instructions,
    isConfirmingCancel,
    isCancelling,
    isPausing,
    isResuming,
    hasActiveDelegation: hasActiveDelegation(inboxEntry),
    handleOpen,
    handleClose,
    handleAskAssistant,
    handleSelectTarget,
    handleSelectTaskType,
    handleSelectLocalRuntime,
    handleSelectLocalModel,
    handleSelectAgentProfile,
    handleSearchChange,
    handleBack,
    handleStartAnotherAgent,
    handleCreateNewAgent,
    handleCloseCreateAgentWizard,
    handlePairDevice,
    handleClosePairDeviceModal,
    handleFixLocalMachineConnection,
    handleDelegate,
    handleInstructionsChange,
    handleDiscussActiveDelegation,
    handleCancelClick,
    handleCancelDismiss,
    handleCancelConfirm,
    handlePause,
    handleResume,
  };
}
